/**
 * Driver's license / ID barcode (PDF417, AAMVA standard — every US state and Canadian province). The barcode text
 * is a header followed by three-letter element IDs: DCS last name, DAC first, DAD middle, DBB date of birth,
 * DBC sex, DAG street, DAI city, DAJ state, DAK zip, DAQ license number, DAU height, DAY eyes, DAZ hair, DAW weight.
 */
export interface LicenseData {
  last: string | null;
  first: string | null;
  middle: string | null;
  suffix: string | null;
  dob: string | null;
  sex: 'M' | 'F' | null;
  street: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  address: string | null;
  dl: string | null;
  dlState: string | null;
  height: string | null;
  heightInches: number | null;
  weight: number | null;
  eyes: string | null;
  hair: string | null;
  expires: string | null;
  dlClass: string | null;
}

const ELEMENT = /^(D[A-Z]{2}|Z[A-Z]{2})(.*)$/;

/** Reads the AAMVA text (from a camera or a handheld 2D scanner). Null when it isn't a license barcode. */
export function parseAamva(raw: string): LicenseData | null {
  if (!raw) return null;
  const text = raw.replace(/\r\n?/g, '\n');
  const at = text.search(/ANSI ?\d{6}|AAMVA\d{6}/);
  if (at < 0 && !/\nDAQ|\nDCS|DAQ[A-Z0-9]/.test(text)) return null;
  const f: Record<string, string> = {};
  // Elements are separated by line feeds (some scanners turn them into other control characters).
  for (const piece of text.split(/[\n\x1e\x1d\x1c]+/)) {
    let line = piece.trim();
    // The first element shares a line with the subfile header (e.g. "DLDAQ123456").
    const sub = /^(?:.*?)(DL|ID)(D[A-Z]{2}.*)$/.exec(line);
    if (sub && /^(ANSI|AAMVA|@)/.test(line)) line = sub[2]!;
    else if (/^(DL|ID)D[A-Z]{2}/.test(line)) line = line.slice(2);
    const m = ELEMENT.exec(line);
    if (m && !(m[1]! in f)) f[m[1]!] = m[2]!.trim();
  }
  if (!f.DAQ && !f.DCS && !f.DAA) return null;
  const clean = (v?: string) => {
    const s = (v ?? '').trim();
    return !s || /^(NONE|UNAVL|UNKNOWN)$/i.test(s) ? null : s;
  };
  let last = clean(f.DCS ?? f.DAB);
  let first = clean(f.DAC ?? f.DCT);
  let middle = clean(f.DAD);
  // Older cards: DAA "LAST,FIRST,MIDDLE".
  if (!last && f.DAA) [last, first, middle] = f.DAA.split(/[,$]/).map((x) => clean(x)) as [string | null, string | null, string | null];
  if (first && !middle && /,/.test(first)) [first, middle] = first.split(',').map((x) => clean(x)) as [string | null, string | null];
  const canadian = /^CAN/i.test(f.DCG ?? '');
  const date = (v?: string) => {
    const s = clean(v);
    if (!s || !/^\d{8}$/.test(s)) return null;
    // US cards: MMDDCCYY; Canadian cards: CCYYMMDD.
    const ymd = canadian || (/^(19|20)\d\d(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])$/.test(s) && !/^(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])(19|20)\d\d$/.test(s));
    return ymd ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}` : `${s.slice(4, 8)}-${s.slice(0, 2)}-${s.slice(2, 4)}`;
  };
  const sex = f.DBC === '1' || /^M/i.test(f.DBC ?? '') ? 'M' : f.DBC === '2' || /^F/i.test(f.DBC ?? '') ? 'F' : null;
  const zip = clean(f.DAK)?.replace(/0000$/, '').replace(/^(\d{5})(\d{4})$/, '$1-$2') ?? null;
  // Height: "070 IN", "5-10", "178 CM".
  let heightInches: number | null = null;
  const h = clean(f.DAU) ?? '';
  const hin = /^(\d{2,3})\s*(IN)?$/i.exec(h);
  const hcm = /^(\d{2,3})\s*CM$/i.exec(h);
  const hft = /^(\d)'?-?(\d{1,2})/.exec(h);
  if (hin) heightInches = +hin[1]!;
  else if (hcm) heightInches = Math.round(+hcm[1]! / 2.54);
  else if (hft) heightInches = +hft[1]! * 12 + +hft[2]!;
  const wRaw = clean(f.DAW);
  const weight = wRaw ? +(wRaw.match(/\d+/)?.[0] ?? '') || null : null;
  const street = [clean(f.DAG), clean(f.DAH)].filter(Boolean).join(' ') || null;
  const city = clean(f.DAI);
  const state = clean(f.DAJ)?.toUpperCase() ?? null;
  return {
    last: last ? titleCase(last) : null,
    first: first ? titleCase(first) : null,
    middle: middle ? titleCase(middle) : null,
    suffix: clean(f.DCU),
    dob: date(f.DBB),
    sex,
    street: street ? titleCase(street) : null,
    city: city ? titleCase(city) : null,
    state,
    zip,
    address: [street && titleCase(street), [city && titleCase(city), state].filter(Boolean).join(', '), zip].filter(Boolean).join(' ') || null,
    dl: clean(f.DAQ)?.replace(/\s+/g, '').toUpperCase() ?? null,
    dlState: state,
    height: heightInches ? `${Math.floor(heightInches / 12)}-${String(heightInches % 12).padStart(2, '0')}` : null,
    heightInches,
    weight,
    eyes: clean(f.DAY)?.toUpperCase() ?? null,
    hair: clean(f.DAZ)?.toUpperCase() ?? null,
    expires: date(f.DBA),
    dlClass: clean(f.DCA)?.toUpperCase() ?? null,
  };
}

function titleCase(s: string) {
  return s.toLowerCase().replace(/\b([a-z])/g, (c) => c.toUpperCase()).replace(/\bMc([a-z])/g, (_, c: string) => `Mc${c.toUpperCase()}`);
}
